import { ArrowLeft, CheckCircle2, KeyRound, RefreshCw, Save } from 'lucide-react'
import { useId, useState, type FormEvent } from 'react'

import { Button, CheckboxField, TextField } from '@krp/ui-kit'

import {
  EMPTY_CONNECTION_FORM_CONTEXT,
  type ConnectionFormContext,
  type ConnectionFormExtension,
} from '../model/connectionForms'
import type { ResourceDraft, ResourceEditorActions, ResourceEditorVm } from '../model/types'
import { ActionReason, CommandError } from './ActionReason'
import { QueryState } from '../../../shared/QueryState'
import '../../../shared/platform.css'

const capabilityLabel = (vm: ResourceEditorVm, name: string): string => vm.capabilityTexts[name]?.label ?? name
const capabilityHint = (vm: ResourceEditorVm, name: string): string | null => vm.capabilityTexts[name]?.hint ?? null

/**
 * Форма ресурса: общее (имя, состояние, права) — здесь, native-настройки —
 * форма пакета вида (`form.Form`). Черновик живёт в состоянии экрана, а не в
 * Redux; секрет — отдельным полем, которое очищается после каждой отправки и
 * никогда не заполняется с сервера (Platform его и не возвращает).
 */
export function ResourceEditorScreen({
  vm,
  actions,
  form,
  context = EMPTY_CONNECTION_FORM_CONTEXT,
}: {
  vm: ResourceEditorVm
  actions: ResourceEditorActions
  form: ConnectionFormExtension | undefined
  context?: ConnectionFormContext
}) {
  const title = vm.mode === 'create' ? `Новый источник: ${vm.kindTitle}` : `Изменить ресурс: ${vm.kindTitle}`
  if (vm.created !== null) {
    // Карточку сервер откроет, только когда rid нового ресурса попадёт в сессию (`resource_id` токена).
    return (
      <section aria-label={title} className="platform-resource-editor">
        <p role="status">
          <CheckCircle2 size={16} aria-hidden="true" /> Источник «{vm.created.name}» создан.
        </p>
        <p>Если карточка ответит «Нет доступа», ресурс станет доступен после выдачи допуска и повторного входа.</p>
        <Button variant="primary" onClick={actions.openCreated} actionId="platform.resource-editor.open-created">
          Открыть карточку
        </Button>
        <Button onClick={actions.cancel} actionId="platform.resource-editor.cancel">
          Закрыть
        </Button>
      </section>
    )
  }
  if (vm.query.status !== 'ready' || vm.initial === null || form === undefined) {
    return (
      <section aria-label={title} className="platform-resource-editor">
        <QueryState query={vm.query} emptyText="Ресурс не найден" onRetry={actions.reload} />
        <ActionReason action={vm.form} />
        <Button onClick={actions.cancel} actionId="platform.resource-editor.cancel">
          Отмена
        </Button>
      </section>
    )
  }
  return <EditorForm title={title} vm={vm} actions={actions} form={form} initial={vm.initial} context={context} />
}

function EditorForm({
  title,
  vm,
  actions,
  form,
  initial,
  context,
}: {
  title: string
  vm: ResourceEditorVm
  actions: ResourceEditorActions
  form: ConnectionFormExtension
  initial: ResourceDraft
  context: ConnectionFormContext
}) {
  const [draft, setDraft] = useState<ResourceDraft>(initial)
  const [secret, setSecret] = useState('')
  const [checked, setChecked] = useState(false)
  const secretId = useId()

  const issues = form.validate(draft.settings)
  const disabledCapabilities = draft.capabilities.filter((capability) => !capability.enabled).length
  const nameMissing = draft.displayName.trim() === ''
  const secretMissing = vm.mode === 'create' && vm.secret?.requiredOnCreate === true && secret === ''
  const invalid = nameMissing || secretMissing || issues.length > 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setChecked(true)
    if (invalid || !vm.save.allowed || vm.saveState.busy) return
    actions.submit({ draft, secret: secret === '' ? null : secret })
    // Секрет не остаётся в форме ни при успехе, ни при отказе: повтор — новым вводом.
    setSecret('')
  }

  const Form = form.Form
  return (
    <form aria-label={title} className="platform-resource-editor" onSubmit={submit} noValidate>
      <h2>{title}</h2>
      {/* Пока команда идёт, форма недоступна целиком: правка во время сохранения потерялась бы. */}
      <fieldset disabled={vm.saveState.busy} className="platform-resource-editor__body">
      <TextField
        label="Название"
        value={draft.displayName}
        onChange={(displayName) => setDraft({ ...draft, displayName })}
      />
      {checked && nameMissing && <p role="alert">Укажите название ресурса</p>}

      {vm.mode === 'edit' && (
        <CheckboxField
          label="Ресурс включён"
          checked={draft.enabled}
          onChange={(enabled) => setDraft({ ...draft, enabled })}
          actionId="platform.resource-editor.enabled"
        />
      )}

      <fieldset className="platform-resource-editor__caps">
        <legend>Права ресурса</legend>
        <p className="platform-capability__hint">
          Какие права бывают, задаёт вид ресурса: служба умеет ровно их. Включено ли право у этого ресурса, хранится в
          Platform и меняется здесь. Кому из людей право выдано, настраивается в разделе «Доступ» по ролям.
        </p>
        {disabledCapabilities > 0 && (
          <>
            <p className="platform-capability__hint">
              Права, которые вид добавил после создания ресурса, Platform записывает выключенными: они ничего не открывают,
              пока вы их не включите. Выключенных сейчас: {disabledCapabilities}.
            </p>
            <Button
              onClick={() =>
                setDraft({ ...draft, capabilities: draft.capabilities.map((capability) => ({ ...capability, enabled: true })) })
              }
              actionId="platform.resource-editor.enable-all-capabilities"
            >
              Включить все права
            </Button>
          </>
        )}
        {draft.capabilities.map((capability, index) => (
          <CheckboxField
            key={capability.name}
            label={
              <span className="platform-capability">
                <span className="platform-capability__title">
                  {capabilityLabel(vm, capability.name)}{' '}
                  <code className="platform-capability__code">{capability.name}</code>
                </span>
                {capabilityHint(vm, capability.name) !== null && (
                  <span className="platform-capability__hint">{capabilityHint(vm, capability.name)}</span>
                )}
              </span>
            }
            checked={capability.enabled}
            onChange={(enabled) =>
              setDraft({
                ...draft,
                capabilities: draft.capabilities.map((item, at) => (at === index ? { ...item, enabled } : item)),
              })
            }
            actionId="platform.resource-editor.capability"
          />
        ))}
      </fieldset>

      <fieldset className="platform-resource-editor__settings">
        <legend>Настройки подключения</legend>
        <Form
          settings={draft.settings}
          onChange={(settings) => setDraft({ ...draft, settings })}
          disabled={vm.saveState.busy}
          issues={checked ? issues : []}
          context={context}
        />
      </fieldset>

      {vm.secret !== null && (
        <div className="krp-field">
          <label className="krp-field__label" htmlFor={secretId}>
            <KeyRound size={14} aria-hidden="true" /> {vm.secret.label}
          </label>
          <input
            id={secretId}
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
          />
          <small>
            {vm.mode === 'edit'
              ? `${vm.secret.hint} Оставьте пустым, чтобы не менять.`
              : vm.secret.hint}
          </small>
          {checked && secretMissing && <p role="alert">Без секрета ресурс не подключится</p>}
        </div>
      )}

      </fieldset>

      <CommandError error={vm.saveState.error} />
      {vm.stale && (
        <Button icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.resource-editor.reload">
          Перечитать ресурс
        </Button>
      )}
      <ActionReason action={vm.save} />
      <div className="platform-resource-editor__commands">
        <Button
          type="submit"
          variant="primary"
          icon={<Save size={16} aria-hidden="true" />}
          busy={vm.saveState.busy}
          disabled={!vm.save.allowed}
          actionId="platform.resource-editor.save"
          disabledReason={vm.save.reason}
        >
          {vm.mode === 'create' ? 'Создать' : 'Сохранить'}
        </Button>
        {vm.mode === 'create' && actions.backToKinds !== undefined && (
          <Button
            icon={<ArrowLeft size={16} aria-hidden="true" />}
            disabled={vm.saveState.busy}
            onClick={actions.backToKinds}
            actionId="platform.resource-editor.back"
          >
            Назад к плиткам
          </Button>
        )}
        <Button onClick={actions.cancel} actionId="platform.resource-editor.cancel">
          Отмена
        </Button>
      </div>
    </form>
  )
}
